import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { displayAmountErrorLabel, OEM_LICENSE } from "components/licenses/common";
import style from "components/licenses/delivery-history/add-license-delivery.scss";
import { License } from "components/licenses/delivery-history/DeliveryFormContent";
import StaticTable from "components/support/api-guide/StaticTable";
import Tooltip from "components/tooltip/Tooltip";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { StoreState } from "store";
import form from "styles/form.scss";
import { roundToOneDecimal } from "utils/format";

import testIds from "testIds.json";

interface Props {
    isEdit: boolean;
    fromBms: boolean;
    availableLicenses: License[];
    setSelectedLicense: (licenses: License) => void;
    selectedLicense: License;
    setLicenseSelectionErrors: (value: boolean) => void;
}

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

const LicenseKeySelectionForm = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();

    React.useEffect(() => {
        if (!props.setLicenseSelectionErrors || !props.selectedLicense) {
            return;
        }

        const hasError =
            displayAmountErrorLabel(props.selectedLicense, t) != null || props.selectedLicense?.totalOfLicenses < 0;
        props.setLicenseSelectionErrors(hasError);
    }, [props.selectedLicense, t, props.setLicenseSelectionErrors]);

    const getDropDownLicensesList = React.useCallback(() => {
        if (props.availableLicenses.length <= 0) {
            return props.selectedLicense ? [props.selectedLicense] : [];
        }
        return props.availableLicenses;
    }, [props.availableLicenses, props.selectedLicense]);

    const LICENSE_TYPES = React.useMemo(
        () => [
            { productName: t("Licenses.bms.assignDialog.licenseTypeValidation"), productId: "bms-validation" },
            { productName: t("Licenses.bms.assignDialog.licenseTypeBbti"), productId: "bms-bbti" },
            { productName: t("Licenses.bms.assignDialog.licenseTypeInsurance"), productId: "bms-insurance" },
            { productName: t("Licenses.bms.assignDialog.licenseTypeNtf"), productId: "bms-ntf" },
            { productName: t("Licenses.bms.assignDialog.licenseTypeLease"), productId: "bms-lease" },
            { productName: t("Licenses.bms.assignDialog.licenseTypeAll"), productId: "bms-all" },
            { productName: t("Licenses.bms.assignDialog.usdk"), productId: "usdk" },
        ],
        [t]
    );

    const handleLicenseTypeChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const selected = getDropDownLicensesList().find((item) => item.licenseType === e.target.value);
            if (selected !== undefined) {
                props.setSelectedLicense(selected);
            }
        },
        [getDropDownLicensesList, props.setSelectedLicense]
    );

    const handleLicensesToAddChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, license: License) => {
            if (props.selectedLicense.index === license.index) {
                const inputValue = e.target.value;
                const licensesToAdd = inputValue === "" ? 0 : parseInt(inputValue) || 0;
                const totalOfLicenses = isNaN(licensesToAdd)
                    ? props.selectedLicense.available
                    : props.selectedLicense.available + licensesToAdd;

                props.setSelectedLicense({
                    ...props.selectedLicense,
                    licensesToAdd,
                    totalOfLicenses,
                });
            }
        },
        [props.selectedLicense, props.setSelectedLicense]
    );

    return (
        <>
            <StaticTable
                headers={[
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.licenseType"),
                    },
                    {
                        value: t("Licenses.bms.assignDialog.keysToAddTable.assignedUsages"),
                    },
                    {
                        value: t("DeliveryHistory.addLicenseDelivery.table.licensesToAdd"),
                    },
                    {
                        value: t("Licenses.bms.assignDialog.keysToAddTable.usageBalance"),
                    },
                    {
                        value: t("Licenses.bms.assignDialog.keysToAddTable.remainingLicenses"),
                    },
                ]}
                cells={[props.selectedLicense].map((license) => {
                    const productName = props.fromBms
                        ? LICENSE_TYPES.find((value) => value.productId === license.licenseType)?.productName
                        : OEM_LICENSE?.productName;
                    return [
                        <Tooltip key={license.licenseType} content={productName}>
                            {props.isEdit ? (
                                <div>{productName}</div>
                            ) : (
                                <select
                                    className={classNames(
                                        form.select,
                                        style.fixedWidthInput,
                                        style.ellipsis,
                                        style.selectArrow
                                    )}
                                    id={"licenseType"}
                                    name={"licenseType"}
                                    key={"licenseType"}
                                    onChange={handleLicenseTypeChange}
                                    onClick={() => {
                                        usageStatisticsService.sendEvent({
                                            category: Category.LICENSE_DELIVERY,
                                            action: Action.CHANGE_LICENSE_TYPE,
                                        });
                                    }}
                                    value={license.licenseType}
                                    data-testid={
                                        props.fromBms
                                            ? testIds.workArea.license.bms.assignKeysDialog.licenseTypeSelect.itself
                                            : testIds.workArea.license.serialNumber.assignKeysView.licenseTypeSelect
                                    }
                                >
                                    {props.availableLicenses
                                        .filter((each) => each.licenseType != "default")
                                        .map((each) => (
                                            <option
                                                key={each.licenseType}
                                                value={each.licenseType}
                                                defaultValue={license.licenseType}
                                                hidden={
                                                    each.licenseType !== license.licenseType &&
                                                    props.selectedLicense.licenseType === each.licenseType
                                                }
                                            >
                                                {props.fromBms
                                                    ? LICENSE_TYPES.find(
                                                          (value) => value.productId === each.licenseType
                                                      )?.productName
                                                    : OEM_LICENSE?.productName}
                                            </option>
                                        ))}
                                </select>
                            )}
                        </Tooltip>,
                        <div
                            key={"available"}
                            className={style.label}
                            data-testid={
                                props.fromBms
                                    ? testIds.workArea.license.bms.assignKeysDialog.availableLabel
                                    : testIds.workArea.license.serialNumber.assignKeysView.availableLabel
                            }
                        >
                            {license.available}
                        </div>,
                        <div key={"assigned"}>
                            <input
                                autoFocus
                                className={classNames(form.input, style.fixedWidth)}
                                key={"added"}
                                id={"added"}
                                type={"number"}
                                onChange={(e) => handleLicensesToAddChange(e, license)}
                                value={license.licensesToAdd.toString()}
                                data-testid={
                                    props.fromBms
                                        ? testIds.workArea.license.bms.assignKeysDialog.assignInput.itself
                                        : testIds.workArea.license.serialNumber.assignKeysView.assignInput.itself
                                }
                            />
                            {displayAmountErrorLabel(
                                license,
                                t,
                                undefined,
                                undefined,
                                props.fromBms
                                    ? testIds.workArea.license.bms.assignKeysDialog.assignInput.errorLabel
                                    : testIds.workArea.license.serialNumber.assignKeysView.assignInput.errorLabel
                            )}
                        </div>,
                        <div
                            key={"total"}
                            className={style.totalLabel}
                            data-testid={
                                props.fromBms
                                    ? testIds.workArea.license.bms.assignKeysDialog.totalLabel.itself
                                    : testIds.workArea.license.serialNumber.assignKeysView.totalLabel.itself
                            }
                        >
                            {license.totalOfLicenses}
                            {license.totalOfLicenses < 0 && (
                                <div
                                    className={classNames(form.error, style.totalLabelError)}
                                    data-testid={
                                        props.fromBms
                                            ? testIds.workArea.license.bms.assignKeysDialog.totalLabel.errorLabel
                                            : testIds.workArea.license.serialNumber.assignKeysView.totalLabel.errorLabel
                                    }
                                >
                                    <span>{t("DeliveryHistory.addLicenseDelivery.validation.totalOfLicenses")}</span>
                                </div>
                            )}
                        </div>,
                        <div
                            key={"remaining"}
                            className={style.label}
                            data-testid={
                                props.fromBms
                                    ? testIds.workArea.license.bms.assignKeysDialog.remainingLabel
                                    : testIds.workArea.license.serialNumber.assignKeysView.remainingLabel
                            }
                        >
                            {license.available > 0 && license.licensesToAdd
                                ? roundToOneDecimal(license.parentAvailableAmount - license.licensesToAdd)
                                : license.parentAvailableAmount}
                        </div>,
                    ];
                })}
            />
        </>
    );
};

export default connector(LicenseKeySelectionForm);
